import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon, User, Sparkles } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';

const AttendanceCalendar = ({ employees, attendance }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedEmployee, setSelectedEmployee] = useState('all');
  const { isDark } = useTheme();

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];
    
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    
    // Add all days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }
    
    return days;
  };

  const getAttendanceForDate = (date) => {
    if (!date) return [];
    // Format date as YYYY-MM-DD in local timezone to avoid UTC conversion issues
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const dateString = `${year}-${month}-${day}`;
    return attendance.filter(a => a.date === dateString);
  };

  const getAttendanceStats = (date) => {
    const dayAttendance = getAttendanceForDate(date);
    const totalEmployees = selectedEmployee === 'all' ? employees.length : 1;
    
    if (selectedEmployee !== 'all') {
      const employeeAttendance = dayAttendance.find(a => a.employeeId === selectedEmployee);
      return {
        present: employeeAttendance?.status === 'present' ? 1 : 0,
        absent: employeeAttendance?.status === 'absent' ? 1 : 0,
        halfDay: employeeAttendance?.status === 'half-day' ? 1 : 0,
        notMarked: employeeAttendance ? 0 : 1
      };
    }
    
    return {
      present: dayAttendance.filter(a => a.status === 'present').length,
      absent: dayAttendance.filter(a => a.status === 'absent').length,
      halfDay: dayAttendance.filter(a => a.status === 'half-day').length,
      notMarked: totalEmployees - dayAttendance.length
    };
  };

  const getDayColor = (date) => {
    if (!date) return '';
    
    const stats = getAttendanceStats(date);
    const totalEmployees = selectedEmployee === 'all' ? employees.length : 1;
    
    if (stats.notMarked === totalEmployees) {
      return 'bg-gray-100 text-gray-500';
    }
    
    if (selectedEmployee !== 'all') {
      if (stats.present > 0) return 'bg-green-100 text-green-800';
      if (stats.halfDay > 0) return 'bg-yellow-100 text-yellow-800';
      if (stats.absent > 0) return 'bg-red-100 text-red-800';
    } else {
      const presentPercentage = (stats.present / totalEmployees) * 100;
      if (presentPercentage >= 80) return 'bg-green-100 text-green-800';
      if (presentPercentage >= 50) return 'bg-yellow-100 text-yellow-800';
      if (presentPercentage > 0) return 'bg-orange-100 text-orange-800';
      if (stats.absent > 0) return 'bg-red-100 text-red-800';
    }
    
    return 'bg-gray-100 text-gray-500';
  };

  const navigateMonth = (direction) => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + direction, 1));
  };

  const isToday = (date) => {
    if (!date) return false;
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const days = getDaysInMonth(currentDate);
  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const CalendarDay = ({ date }) => {
    if (!date) {
      return <div className={`h-20 sm:h-24 border transition-colors ${isDark ? 'border-gray-700' : 'border-gray-100'}`}></div>;
    }

    const stats = getAttendanceStats(date);
    const dayColor = getDayColor(date);
    const today = isToday(date);

    return (
      <div className={`h-20 sm:h-24 border p-2 relative transition-all duration-300 hover:scale-105 hover:z-10 group ${
        isDark ? 'border-gray-700' : 'border-gray-200'
      } ${
        today 
          ? 'ring-2 ring-offset-2 ring-blue-500 shadow-lg shadow-blue-500/50 animate-pulse-glow' 
          : 'hover:shadow-lg'
      } ${dayColor}`}>
        {/* Today's special background effect */}
        {today && (
          <>
            <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 via-purple-500/10 to-pink-500/10 rounded-lg animate-gradient"></div>
            <div className="absolute top-0 right-0 w-8 h-8">
              <Sparkles className="w-4 h-4 text-yellow-400 animate-bounce absolute top-1 right-1" style={{ animationDelay: '0s' }} />
            </div>
          </>
        )}
        
        <div className="flex items-start justify-between h-full relative z-10">
          {/* Date number with special styling for today */}
          <div className="flex flex-col items-start">
            {today ? (
              <div className="relative">
                <div className="absolute -inset-1 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full blur opacity-75 animate-pulse"></div>
                <span className="relative flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-br from-blue-500 to-purple-600 text-white rounded-full font-bold text-sm sm:text-base shadow-lg transform transition-transform group-hover:scale-110">
                  {date.getDate()}
                </span>
              </div>
            ) : (
              <span className={`text-sm sm:text-base font-medium transition-all ${
                isDark ? 'text-gray-300' : 'text-gray-700'
              } group-hover:font-bold group-hover:scale-110`}>
                {date.getDate()}
              </span>
            )}
          </div>
          
          {/* Attendance indicators */}
          {(stats.present > 0 || stats.absent > 0 || stats.halfDay > 0) && (
            <div className="text-xs space-y-1 animate-slide-up">
              {selectedEmployee === 'all' ? (
                <>
                  {stats.present > 0 && (
                    <div className="flex items-center space-x-1 text-green-600 font-semibold">
                      <div className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></div>
                      <span>P:{stats.present}</span>
                    </div>
                  )}
                  {stats.halfDay > 0 && (
                    <div className="flex items-center space-x-1 text-yellow-600 font-semibold">
                      <div className="w-1.5 h-1.5 bg-yellow-500 rounded-full animate-pulse"></div>
                      <span>H:{stats.halfDay}</span>
                    </div>
                  )}
                  {stats.absent > 0 && (
                    <div className="flex items-center space-x-1 text-red-600 font-semibold">
                      <div className="w-1.5 h-1.5 bg-red-500 rounded-full animate-pulse"></div>
                      <span>A:{stats.absent}</span>
                    </div>
                  )}
                </>
              ) : (
                <div className="w-2 h-2 sm:w-3 sm:h-3 rounded-full bg-current shadow-lg animate-pulse"></div>
              )}
            </div>
          )}
        </div>
        
        {/* Today indicator badge */}
        {today && (
          <div className="absolute bottom-1 left-1/2 transform -translate-x-1/2">
            <div className="px-2 py-0.5 bg-gradient-to-r from-blue-500 to-purple-500 text-white text-[10px] font-bold rounded-full shadow-lg animate-bounce">
              TODAY
            </div>
          </div>
        )}
      </div>
    );
  };

  const Legend = () => (
    <div className="bg-white rounded-lg shadow p-4">
      <h3 className="font-semibold text-gray-800 mb-3">Legend</h3>
      <div className="grid grid-cols-2 gap-3 text-sm">
        <div className="flex items-center space-x-2">
          <div className="w-4 h-4 bg-green-100 border border-green-200 rounded"></div>
          <span>High Attendance (80%+)</span>
        </div>
        <div className="flex items-center space-x-2">
          <div className="w-4 h-4 bg-yellow-100 border border-yellow-200 rounded"></div>
          <span>Medium Attendance (50-79%)</span>
        </div>
        <div className="flex items-center space-x-2">
          <div className="w-4 h-4 bg-orange-100 border border-orange-200 rounded"></div>
          <span>Low Attendance (1-49%)</span>
        </div>
        <div className="flex items-center space-x-2">
          <div className="w-4 h-4 bg-red-100 border border-red-200 rounded"></div>
          <span>No Attendance</span>
        </div>
        <div className="flex items-center space-x-2">
          <div className="w-4 h-4 bg-gray-100 border border-gray-200 rounded"></div>
          <span>Not Marked</span>
        </div>
        <div className="flex items-center space-x-2">
          <div className="w-4 h-4 border-2 border-blue-500 rounded"></div>
          <span>Today</span>
        </div>
      </div>
      
      {selectedEmployee === 'all' && (
        <div className="mt-3 pt-3 border-t">
          <p className="text-xs text-gray-600">
            Numbers show: P=Present, H=Half Day, A=Absent
          </p>
        </div>
      )}
    </div>
  );

  const MonthlyStats = () => {
    const monthAttendance = attendance.filter(a => {
      const attendanceDate = new Date(a.date);
      return attendanceDate.getMonth() === currentDate.getMonth() &&
             attendanceDate.getFullYear() === currentDate.getFullYear();
    });

    let stats;
    if (selectedEmployee === 'all') {
      stats = {
        present: monthAttendance.filter(a => a.status === 'present').length,
        absent: monthAttendance.filter(a => a.status === 'absent').length,
        halfDay: monthAttendance.filter(a => a.status === 'half-day').length
      };
    } else {
      const employeeAttendance = monthAttendance.filter(a => a.employeeId === selectedEmployee);
      stats = {
        present: employeeAttendance.filter(a => a.status === 'present').length,
        absent: employeeAttendance.filter(a => a.status === 'absent').length,
        halfDay: employeeAttendance.filter(a => a.status === 'half-day').length
      };
    }

    return (
      <div className="bg-white rounded-lg shadow p-4">
        <h3 className="font-semibold text-gray-800 mb-3">
          {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()} Summary
        </h3>
        <div className="grid grid-cols-3 gap-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-green-600">{stats.present}</div>
            <div className="text-sm text-gray-600">Present Days</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-yellow-600">{stats.halfDay}</div>
            <div className="text-sm text-gray-600">Half Days</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-red-600">{stats.absent}</div>
            <div className="text-sm text-gray-600">Absent Days</div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Attendance Calendar</h2>
          <p className="text-gray-600">View attendance patterns over time</p>
        </div>
        
        <div className="flex items-center space-x-4">
          <select
            value={selectedEmployee}
            onChange={(e) => setSelectedEmployee(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="all">All Employees</option>
            {employees.map(employee => (
              <option key={employee.id} value={employee.id}>
                {employee.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Calendar Navigation */}
      <div className="bg-white rounded-lg shadow">
        <div className="flex items-center justify-between p-4 border-b">
          <button
            onClick={() => navigateMonth(-1)}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          
          <h3 className="text-xl font-semibold text-gray-800">
            {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
          </h3>
          
          <button
            onClick={() => navigateMonth(1)}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>

        {/* Day Headers */}
        <div className="grid grid-cols-7 border-b">
          {dayNames.map((dayName) => (
            <div key={dayName} className="p-3 text-center font-medium text-gray-600 bg-gray-50">
              {dayName}
            </div>
          ))}
        </div>

        {/* Calendar Days */}
        <div className="grid grid-cols-7">
          {days.map((date, index) => (
            <CalendarDay key={index} date={date} />
          ))}
        </div>
      </div>

      {/* Stats and Legend */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <MonthlyStats />
        <Legend />
      </div>
    </div>
  );
};

export default AttendanceCalendar;